import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { NonFinancialDriverTreeNode } from '../../../types';
import { useDriversView } from '../../context/drivers-view-context';
import { DriverGroupFormScreen } from './driver-group-form-screen';
import { DriverGroupLoadingScreen } from './driver-group-loading-screen';
import { DriverGroupSuccessScreen } from './driver-group-success-screen';
import { useDriversGroupForm } from './use-drivers-group-form';
import { buildDriverGroupOrderingPayload } from './utils';

type Screen = 'form' | 'saving' | 'saved';

export interface DriverGroupWizardContainerProps {
  onClose: () => void;
  row?: NonFinancialDriverTreeNode;
}

export const DriverGroupWizardContainer = ({ onClose, row }: DriverGroupWizardContainerProps) => {
  const {
    driverTree,
    createGroup,
    updateGroupAndOrdering,
    isCreatingGroup,
    isUpdatingGroupAndOrdering,
    errorCreatingGroup,
    errorUpdatingGroupAndOrdering
  } = useDriversView();

  const [screen, setScreen] = useState<Screen>('form');
  const [error, setError] = useState<string | undefined>();

  const intl = useIntl();

  const {
    groupName,
    setGroupName,
    selectedDrivers,
    setSelectedDrivers,
    availableDrivers,
    errors,
    validate,
    revalidate,
    isEditMode
  } = useDriversGroupForm(driverTree, row);

  const isSaving = isCreatingGroup || isUpdatingGroupAndOrdering;
  const hasError = errorCreatingGroup || errorUpdatingGroupAndOrdering;

  useEffect(() => {
    if (hasError) {
      setError(
        intl.formatMessage({
          id: 'reporting.try_again',
          defaultMessage: 'Try again.'
        })
      );
      setScreen('form');
    }
  }, [intl, hasError]);

  useEffect(() => {
    if (isSaving) {
      setScreen('saving');
    }
  }, [isSaving]);

  const handleSubmit = useCallback(() => {
    if (!validate()) {
      return;
    }
    setError(undefined);

    if (!driverTree) {
      return;
    }

    const driverIds = selectedDrivers.map(driver => driver.id);

    if (isEditMode && row) {
      // For edit mode, update group name and driver membership in parallel
      const orderingPayload = buildDriverGroupOrderingPayload(row.id, selectedDrivers, driverTree);

      updateGroupAndOrdering(row.id, { name: groupName.trim() }, orderingPayload, {
        onSuccess: () => setScreen('saved'),
        onError: () => setScreen('form')
      });
    } else {
      // For create mode, include driver_ids in the initial creation
      createGroup(
        { name: groupName.trim(), driver_ids: driverIds },
        {
          onSuccess: () => setScreen('saved'),
          onError: () => setScreen('form')
        }
      );
    }
  }, [groupName, selectedDrivers, validate, isEditMode, row, driverTree, updateGroupAndOrdering, createGroup]);

  return (
    <>
      {screen === 'form' && (
        <DriverGroupFormScreen
          error={error}
          validationErrors={errors}
          groupName={groupName}
          onGroupNameChange={setGroupName}
          onClose={onClose}
          onSubmit={handleSubmit}
          onBlur={revalidate}
          isEditMode={isEditMode}
          availableDrivers={availableDrivers}
          selectedDrivers={selectedDrivers}
          setSelectedDrivers={setSelectedDrivers}
          revalidate={revalidate}
        />
      )}
      {screen === 'saving' && <DriverGroupLoadingScreen isEditMode={isEditMode} />}
      {screen === 'saved' && <DriverGroupSuccessScreen onClose={onClose} isEditMode={isEditMode} />}
    </>
  );
};
